<template>
  <div class="product-detail-page">
    <div class="container">
      <div v-if="loading" class="loading">
        Loading product...
      </div>

      <div v-else-if="error" class="error">
        {{ error }}
      </div>

      <div v-else-if="product" class="product-detail">
        <div class="product-images">
          <div class="main-image">
            <img
              v-if="selectedImage"
              :src="selectedImage.image_url"
              :alt="product.name"
            />
            <div v-else class="no-image">
              No Image
            </div>
          </div>

          <div v-if="product.images && product.images.length > 1" class="image-thumbnails">
            <div
              v-for="image in product.images"
              :key="image.id"
              class="thumbnail"
              :class="{ active: selectedImage && selectedImage.id === image.id }"
              @click="selectedImage = image"
            >
              <img :src="image.image_url" :alt="product.name" />
            </div>
          </div>
        </div>

        <div class="product-info-section">
          <h1 class="product-title">
            {{ product.name }}
          </h1>

          <div class="product-price-section">
            <div v-if="product.is_on_sale" class="sale-badge-large">
              SALE - SAVE {{ product.discount_percentage }}%
            </div>

            <div class="price-display">
              <span v-if="product.is_on_sale" class="price-regular">
                ${{ formatPrice(regularPrice) }}
              </span>
              <span class="price-current" :class="{ 'price-sale': product.is_on_sale }">
                ${{ formatPrice(currentPrice) }}
              </span>
            </div>

            <div v-if="product.is_on_sale" class="sale-message">
              🎉 Limited time offer! Save ${{ formatPrice(regularPrice - currentPrice) }}
            </div>
          </div>

          <div class="product-description">
            <p>{{ product.description }}</p>
          </div>

          <div v-if="product.variants && product.variants.length > 0" class="variant-selection">
            <!-- Color Selection -->
            <div v-if="availableColors.length > 0" class="variant-group">
              <label>Color:</label>
              <div class="variant-options">
                <button
                  v-for="color in availableColors"
                  :key="color"
                  class="variant-btn"
                  :class="{ active: selectedColor === color }"
                  @click="selectedColor = color"
                >
                  {{ color }}
                </button>
              </div>
            </div>

            <!-- Size Selection -->
            <div v-if="availableSizes.length > 0" class="variant-group">
              <label>Size:</label>
              <div class="variant-options">
                <button
                  v-for="size in availableSizes"
                  :key="size"
                  class="variant-btn"
                  :class="{ active: selectedSize === size }"
                  @click="selectedSize = size"
                >
                  {{ size }}
                </button>
              </div>
            </div>
          </div>

          <div class="quantity-section">
            <label>Quantity:</label>
            <div class="quantity-control">
              <button :disabled="quantity <= 1" @click="decrementQuantity">
                -
              </button>
              <input v-model.number="quantity" type="number" min="1" />
              <button @click="incrementQuantity">
                +
              </button>
            </div>
          </div>

          <div class="action-buttons">
            <button
              class="btn-add-to-cart"
              :disabled="!selectedVariant || !isAuthenticated"
              @click="addToCart"
            >
              {{ isAuthenticated ? 'Add to Cart' : 'Login to Purchase' }}
            </button>

            <button class="btn-back" @click="$router.push('/shop')">
              Back to Shop
            </button>
          </div>

          <div v-if="!isAuthenticated" class="auth-notice">
            Please login to add items to your cart.
          </div>

          <div v-if="addedToCart" class="success-message">
            Added to cart!
            <router-link to="/cart">
              View Cart
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import ProductRepository from '../data/product.repository'

export default {
  name: 'ProductDetail',
  data () {
    return {
      product: null,
      loading: false,
      error: null,
      selectedImage: null,
      selectedColor: null,
      selectedSize: null,
      quantity: 1,
      addedToCart: false
    }
  },
  computed: {
    ...mapState(['currentUser']),
    ...mapGetters(['cartItemCount']),
    isAuthenticated () {
      return !!this.currentUser
    },
    availableColors () {
      if (!this.product || !this.product.variants) return []
      const colors = this.product.variants
        .filter(v => v.active === 1 && v.color)
        .map(v => v.color)
      return [...new Set(colors)]
    },
    availableSizes () {
      if (!this.product || !this.product.variants) return []
      const sizes = this.product.variants
        .filter(v => v.active === 1 && v.size)
        .map(v => v.size)
      return [...new Set(sizes)]
    },
    selectedVariant () {
      if (!this.product || !this.product.variants) return null

      return this.product.variants.find(v => {
        const colorMatch = !this.selectedColor || v.color === this.selectedColor
        const sizeMatch = !this.selectedSize || v.size === this.selectedSize
        return v.active === 1 && colorMatch && sizeMatch
      })
    },
    currentPrice () {
      if (!this.product) return 0

      // If product is on sale, use sale price (ignoring variant modifiers)
      if (this.product.is_on_sale && this.product.sale_price_cents !== null) {
        return this.product.sale_price_cents
      }

      // Otherwise use base price + variant modifier
      if (this.selectedVariant && this.selectedVariant.price_modifier_cents) {
        return this.product.base_price_cents + this.selectedVariant.price_modifier_cents
      }

      return this.product.base_price_cents
    },
    regularPrice () {
      if (!this.product) return 0

      if (this.selectedVariant && this.selectedVariant.price_modifier_cents) {
        return this.product.base_price_cents + this.selectedVariant.price_modifier_cents
      }

      return this.product.base_price_cents
    }
  },
  mounted () {
    this.loadProduct()
  },
  methods: {
    loadProduct () {
      this.loading = true
      this.error = null

      const slug = this.$route.params.slug

      ProductRepository.getBySlug(slug)
        .then((product) => {
          this.product = product
          this.loading = false

          // Set default image
          if (product.images && product.images.length > 0) {
            this.selectedImage = product.images.find(img => img.is_primary === 1) || product.images[0]
          }

          // Set default selections
          if (this.availableColors.length > 0) {
            this.selectedColor = this.availableColors[0]
          }
          if (this.availableSizes.length > 0) {
            this.selectedSize = this.availableSizes[0]
          }
        })
        .catch((err) => {
          this.loading = false
          this.error = 'Product not found'
          console.error('Error loading product:', err)
        })
    },
    addToCart () {
      if (!this.selectedVariant) {
        alert('Please select all options')
        return
      }

      if (!this.isAuthenticated) {
        alert('Please log in or register before adding items to your cart.')
        this.$router.push('/register')
        return
      }

      const cartItem = {
        variantId: this.selectedVariant.id,
        productId: this.product.id,
        quantity: this.quantity,
        price: this.currentPrice / 100, // Uses computed currentPrice (respects sale)
        name: this.product.name,
        size: this.selectedVariant.size,
        color: this.selectedVariant.color,
        image: this.selectedImage ? this.selectedImage.image_url : null,
        isOnSale: this.product.is_on_sale || false
      }

      this.$store.dispatch('addToCart', cartItem)

      this.addedToCart = true
    },
    incrementQuantity () {
      this.quantity++
    },
    decrementQuantity () {
      if (this.quantity > 1) {
        this.quantity--
      }
    },
    formatPrice (cents) {
      return (cents / 100).toFixed(2)
    }
  }
}
</script>

<style scoped>
.product-detail-page {
  padding: 20px 0;
  min-height: 60vh;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.loading,
.error {
  text-align: center;
  padding: 40px 20px;
  font-size: 18px;
}

.error {
  color: #e53e3e;
}

.product-detail {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  margin-top: 4px;
}

.product-images {
  position: sticky;
  top: 20px;
  height: fit-content;
}

.main-image {
  width: 100%;
  height: 500px;
  background: #f7fafc;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.main-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-thumbnails {
  display: flex;
  gap: 10px;
  margin-top: 10px;
  overflow-x: auto;
}

.thumbnail {
  width: 80px;
  height: 80px;
  border: 2px solid transparent;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  flex-shrink: 0;
}

.thumbnail.active {
  border-color: #3182ce;
}

.thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-info-section {
  padding: 20px 0;
}

.product-title {
  font-size: 32px;
  font-weight: bold;
  margin: 0 0 16px 0;
}

/* Sale Pricing Section */
.product-price-section {
  margin-bottom: 18px;
}

.sale-badge-large {
  display: inline-block;
  background: linear-gradient(135deg, #e53e3e 0%, #c53030 100%);
  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(229, 62, 62, 0.3);
}

.price-display {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.price-regular {
  font-size: 22px;
  color: #a0aec0;
  text-decoration: line-through;
}

.price-current {
  font-size: 32px;
  font-weight: bold;
  color: #2b6cb0;
}

.price-sale {
  color: #e53e3e !important;
}

.sale-message {
  background: #fef5e7;
  border-left: 4px solid #f39c12;
  padding: 12px;
  border-radius: 4px;
  font-size: 14px;
  color: #2d3748;
  font-weight: 600;
}

.product-description {
  color: #4a5568;
  line-height: 1.6;
  margin-bottom: 18px;
}

.variant-selection {
  margin-bottom: 18px;
}

.variant-group {
  margin-bottom: 16px;
}

.variant-group label {
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
  color: #2d3748;
}

.variant-options {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.variant-btn {
  padding: 10px 20px;
  border: 2px solid #e2e8f0;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
}

.variant-btn:hover {
  border-color: #3182ce;
}

.variant-btn.active {
  border-color: #3182ce;
  background: #3182ce;
  color: white;
}

.quantity-section {
  margin-bottom: 18px;
}

.quantity-section label {
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
  color: #2d3748;
}

.quantity-control {
  display: flex;
  align-items: center;
  gap: 10px;
}

.quantity-control button {
  width: 36px;
  height: 36px;
  border: 1px solid #cbd5e0;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 18px;
}

.quantity-control button:hover:not(:disabled) {
  background: #f7fafc;
}

.quantity-control button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.quantity-control input {
  width: 60px;
  height: 36px;
  text-align: center;
  border: 1px solid #cbd5e0;
  border-radius: 4px;
  font-size: 16px;
}

.action-buttons {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.btn-add-to-cart,
.btn-back,
.btn-checkout {
  flex: 1;
  padding: 14px 24px;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-add-to-cart {
  background: #48bb78;
  color: white;
}

.btn-add-to-cart:hover:not(:disabled) {
  background: #38a169;
}

.btn-add-to-cart:disabled {
  background: #cbd5e0;
  cursor: not-allowed;
}

.btn-back {
  background: #e2e8f0;
  color: #2d3748;
}

.btn-back:hover {
  background: #cbd5e0;
}

.btn-checkout {
  background: #3182ce;
  color: white;
}

.btn-checkout:hover {
  background: #2c5aa0;
}

.auth-notice {
  padding: 12px;
  background: #fef5e7;
  border-left: 4px solid #f39c12;
  border-radius: 4px;
  font-size: 14px;
}

.auth-notice a {
  color: #2b6cb0;
  font-weight: 600;
}

.success-message {
  padding: 12px;
  background: #d4edda;
  border-left: 4px solid #28a745;
  border-radius: 4px;
  color: #155724;
  font-weight: 600;
}

.success-message a {
  color: #0c3d1a;
  text-decoration: underline;
}

@media (max-width: 768px) {
  .product-detail {
    grid-template-columns: 1fr;
    gap: 0px;
  }

  .product-images {
    position: static;
  }

  .main-image {
    height: 350px;
  }

  .product-title {
    font-size: 24px;
  }

  .action-buttons {
    flex-direction: column;
  }
}
</style>



// WEBPACK FOOTER //
// ProductDetail.vue?d0980374